import { useState } from 'react'
import { ConnectableAccount, IntegrationAuth, IntegrationAuthError } from 'api/clients/IntegrationAuthsClient'
import { showToastMessage, showIntegrationSuccessToast } from 'common/ToastMessage/ShowToastMessage'
import { showIntegrationErrorToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useModals } from 'almanac-ui/Modal'
import { useRootStore } from 'stores/RootStore'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useMutation, useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateExternalIntegration } from 'screens/OAuthRedirectV2/hooks'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { createIntegrationAuths } from 'components/Onboarding/CreateBrandStyleModal/CreateBrandStyleModal'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SelectConnectableAccountsDrawer } from 'components/SelectConnectableAccountsDrawer/SelectConnectableAccountsDrawer'
import { SelectConnectableAccountsModal } from 'components/SelectConnectableAccountsModal/SelectConnectableAccountsModal'
import { NoAccountsFoundDrawer } from 'screens/OAuthRedirectV2/NoAccountsFound/NoAccountsFoundDrawer'
import { NoAccountsFoundModal } from 'screens/OAuthRedirectV2/NoAccountsFound/NoAccountsFoundModal'
import { fetchConnectableAccounts } from 'hooks/queries/workspaces/integrationAuths/useConnectableAccounts'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useOauthRefresh } from 'hooks/useOauthRefresh'
import { MetaAdsAccountSetupModal } from 'components/MetaAdsAccountSetupModal/MetaAdsAccountSetupModal'
import { useShowIntegrationSuccessModal } from 'screens/OAuthRedirectV2/hooks'
import { OAUTH_BROADCAST_CHANNEL, OAUTH_MESSAGE_TYPE } from 'constants/oauth'
import { PurchaseAdditionalAccountModal } from 'components/PurchaseAdditionalAccountModal/PurchaseAdditionalAccountModal'

interface Props {
  provider?: ExternalIntegrationEnum
  isSingleSelect?: boolean
  allowReconnect?: boolean
  showToast?: boolean
  onConnecting?: () => void
  onConnected?: () => void
}

export interface ConnectProps {
  onConnect?: (externalIntegrations: ExternalIntegration[], existingIntegration?: ExternalIntegration) => void
  onIntegrationAuthCreated?: (integrationAuth: IntegrationAuth) => void
  switchFromIntegrationId?: number
  isAdditionalAccount?: boolean
}

export const useConnectAccountPopup = (props?: Props) => {
  const { showToast = true } = props || {}
  const { openModal, closeStack, closeModal } = useModals()
  const { drawersStore } = useRootStore()
  const { openDrawer, closeDrawer } = drawersStore
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const currentWorkspace = useCurrentWorkspace()
  const { handleRefresh } = useOauthRefresh()
  const { data: externalIntegrationsData } = useExternalIntegrations({
    select: (data) => {
      return {
        external_integrations: data.external_integrations.filter((externalIntegration) =>
          [
            ExternalIntegrationEnum.Facebook,
            ExternalIntegrationEnum.Instagram,
            ExternalIntegrationEnum.LinkedIn,
            ExternalIntegrationEnum.Google,
            ExternalIntegrationEnum.X,
            ExternalIntegrationEnum.Mailchimp,
            ExternalIntegrationEnum.GoHighLevel,
            ExternalIntegrationEnum.TikTok,
            ExternalIntegrationEnum.YouTube,
            ExternalIntegrationEnum.Shopify,
          ].includes(externalIntegration.provider),
        ),
      }
    },
  })
  const { mutateAsync: createExternalIntegration } = useCreateExternalIntegration()
  const { isMobile } = useResponsiveDesign()
  const { mutateAsync: createIntegrationAuth } = useMutation(createIntegrationAuths)
  const [isConnecting, setIsConnecting] = useState(false)
  const showIntegrationSuccessModal = useShowIntegrationSuccessModal()

  // similar to handleSubmit in OAuthRedirectV2.tsx
  const handleConnectAccount =
    (
      integrationAuthId: number,
      provider: ExternalIntegrationEnum,
      onConnect?: (externalIntegrations: ExternalIntegration[], existingIntegration?: ExternalIntegration) => void,
      switchFromIntegrationId?: number,
      isAdditionalAccount?: boolean,
    ) =>
    async (accounts: ConnectableAccount[]) => {
      props?.onConnecting?.()
      const dismiss = showToast ? showToastMessage('Connecting...', 'loading') : () => {}
      const alreadyConnected = externalIntegrationsData?.external_integrations || []
      const newAccounts = accounts.filter(
        (account) => !alreadyConnected.some((integration) => integration.provider_id === account.provider_id),
      )

      const results = await Promise.all(
        newAccounts.map((account) =>
          createExternalIntegration({
            workspaceId,
            params: {
              ...account,
              integration_auth_id: integrationAuthId,
              ...(switchFromIntegrationId && { switch_from_integration_id: switchFromIntegrationId }),
              ...(isAdditionalAccount && { is_additional_account: true }),
            },
          }),
        ),
      )

      dismiss()

      const successfulResults = results.filter((res) => res.type === SUCCESS_RESPONSE_TYPE)
      const failedResults = results.filter((res) => res.type !== SUCCESS_RESPONSE_TYPE)

      // Show error for any failed results
      if (failedResults.length > 0) {
        showIntegrationErrorToast('Failed to connect account')
        setIsConnecting(false)
        return
      }

      // All succeeded
      const selectedIntegrations = successfulResults.map((res) => res.data.external_integration)

      // Invalidate the external integrations query to update the cache
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])

      if (selectedIntegrations.length > 0) {
        props?.onConnected?.()
        if (showToast) showIntegrationSuccessToast(`Successfully connected to ${selectedIntegrations[0]?.name}!`)
      }

      isMobile ? closeDrawer() : closeModal()

      // Check if backend says purchase is required
      const requiresPurchase = selectedIntegrations.some((integration) => integration.requires_purchase)

      const onSuccessModalClose = () => {
        onConnect?.(selectedIntegrations)
        setIsConnecting(false)
      }

      const showSuccessModalForIntegration = () => {
        const modalShown = showIntegrationSuccessModal({
          provider,
          result: results[0],
          onClose: onSuccessModalClose,
        })

        if (!modalShown) {
          onConnect?.(selectedIntegrations)
          setIsConnecting(false)
        }
      }

      // If backend says purchase is required, show purchase modal first
      if (requiresPurchase && selectedIntegrations.length > 0) {
        const integration = selectedIntegrations[0]
        openModal(PurchaseAdditionalAccountModal, {
          integration,
          onClose: () => {},
          onPurchase: showSuccessModalForIntegration,
        })
        return
      }

      // Otherwise, just show success modal directly
      showSuccessModalForIntegration()
    }

  const connect = (
    popup: Window | undefined,
    options?: ConnectProps & { createdIntegration?: ExternalIntegration },
  ) => {
    setIsConnecting(true)

    // Handle non-OAuth providers like WordPress that don't have a popup window
    // The integration is already created, so we just need to get it and call onConnect
    if (!popup) {
      // If the created integration was passed in options, use it directly
      if (options?.createdIntegration) {
        const integration = options.createdIntegration

        // Check if the backend says purchase is required
        if (integration.requires_purchase) {
          openModal(PurchaseAdditionalAccountModal, {
            integration,
            onClose: () => {},
            onPurchase: () => options?.onConnect?.([integration]),
          })
        } else {
          options?.onConnect?.([integration])
        }

        setIsConnecting(false)
        return
      }
      return
    }

    let safetyTimer: NodeJS.Timeout | null = null
    let visibilityHandler: (() => void) | null = null
    let hasReceivedMessage = false

    // Create BroadcastChannel for OAuth communication
    const oauthChannel = new BroadcastChannel(OAUTH_BROADCAST_CHANNEL)

    if (isMobile) {
      let hasLeftPage = false

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          hasLeftPage = true
        } else if (document.visibilityState === 'visible' && hasLeftPage) {
          setTimeout(() => {
            setIsConnecting(false)
          }, 2000)
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
      visibilityHandler = handleVisibilityChange

      safetyTimer = setTimeout(() => {
        setIsConnecting(false)
        oauthChannel.close()
      }, 30000)
    } else {
      // Safety timeout for non-mobile
      safetyTimer = setTimeout(() => {
        window.removeEventListener('message', listener)
        oauthChannel.close()
        safetyTimer && clearTimeout(safetyTimer)
        visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
        setIsConnecting(false)
      }, 120000) // 2 minutes
    }

    const handleOAuthMessage = async (data: any) => {
      // Handle error case
      if (data.error) {
        if (hasReceivedMessage) return
        hasReceivedMessage = true
        popup.close()
        oauthChannel.close()
        safetyTimer && clearTimeout(safetyTimer)
        visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
        showIntegrationErrorToast(data.error)
        setIsConnecting(false)
        return
      }

      // Validate OAuth data
      if (!data.params || !data.provider) return
      if (hasReceivedMessage) return

      hasReceivedMessage = true
      const { params, provider, accountToRefreshId } = data

      if (data.params.error) {
        popup.close()
        showIntegrationErrorToast('There was an error connecting the account. Please try again.')
        return
      }

      if (accountToRefreshId && params && Object.keys(params).length > 0) {
        await handleRefresh(params, provider, accountToRefreshId, (integration) => {
          options?.onConnect?.([integration])
        })

        popup.close()
        setIsConnecting(false)
        return
      }

      let skipFinallyCleanup = false

      try {
        const { data: integrationAuth } = await createIntegrationAuth({
          provider,
          params,
          workspaceId,
        })

        if (options?.onIntegrationAuthCreated) {
          popup.close()
          return options.onIntegrationAuthCreated(integrationAuth)
        }

        const connectableAccountsData = await queryClient.fetchQuery<{ data: ConnectableAccount[]; metadata: any }>(
          ['workspaces', workspaceId, 'integrationAuths', integrationAuth.id, 'connectableAccounts'],
          () => fetchConnectableAccounts(integrationAuth.id, workspaceId),
        )

        const connectableAccounts = connectableAccountsData.data
        const metadata = connectableAccountsData.metadata

        if (connectableAccounts.length === 0) {
          popup.close()
          isMobile
            ? openDrawer(NoAccountsFoundDrawer, { provider, onClose: closeDrawer })
            : openModal(NoAccountsFoundModal, { provider, onClose: closeModal })
          return
        }

        // When switching to an exisiting shopify intgration, we simply apply the brand plan tactic
        if (
          options?.switchFromIntegrationId &&
          externalIntegrationsData?.external_integrations &&
          provider === ExternalIntegrationEnum.Shopify
        ) {
          const existingIntegration = externalIntegrationsData.external_integrations.find(
            (integration) => integration.provider === provider,
          )

          if (existingIntegration) {
            const matchingAccount = connectableAccounts.find(
              (account) => account.provider_id === existingIntegration.provider_id,
            )

            if (matchingAccount) {
              options?.onConnect?.([], existingIntegration)
              popup.close()
              if (showToast) showIntegrationSuccessToast(`Successfully connected to ${existingIntegration.name}!`)
              return
            }
          }
        }

        // Special handling for Meta Ads - close popup immediately after opening modal
        if (provider === ExternalIntegrationEnum.MetaAds) {
          popup.close()
          openModal(MetaAdsAccountSetupModal, {
            connectableAccounts,
            metadata,
            onCancel: () => {
              closeStack()
            },
            handleSubmit: handleConnectAccount(
              integrationAuth.id,
              provider,
              options?.onConnect,
              options?.switchFromIntegrationId,
              options?.isAdditionalAccount,
            ),
          })
          return
        }

        // For other providers, close the popup immediately
        popup.close()

        const connectProps = {
          connectableAccounts,
          provider,
          onCancel: () => {},
          handleSubmit: handleConnectAccount(
            integrationAuth.id,
            provider,
            options?.onConnect,
            options?.switchFromIntegrationId,
            options?.isAdditionalAccount,
          ),
          isSingleSelect: props?.isSingleSelect || currentWorkspace?.is_virtual_marketer,
          allowReconnect: props?.allowReconnect,
        }

        isMobile
          ? openDrawer(SelectConnectableAccountsDrawer, connectProps)
          : openModal(
              SelectConnectableAccountsModal,
              {
                ...connectProps,
                onCancel: () => {
                  closeStack()
                },
              },
              { newStack: true },
            )
      } catch (e) {
        popup.close()

        if (e instanceof IntegrationAuthError && e.errorCode === 'site_not_published') {
          skipFinallyCleanup = true
          showIntegrationErrorToast(
            'Your Wix site must be published before connecting. Please publish your site and try again.',
          )
          // Delay cleanup to let user read the message (matches OAuthRedirectV2 behavior)
          setTimeout(() => {
            oauthChannel.close()
            safetyTimer && clearTimeout(safetyTimer)
            visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
            setIsConnecting(false)
          }, 4000)
          return
        }

        showIntegrationErrorToast('There was an error connecting the account. Please try again.')
        reportError(e)
      } finally {
        if (!skipFinallyCleanup) {
          oauthChannel.close()
          safetyTimer && clearTimeout(safetyTimer)
          visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
          setIsConnecting(false)
        }
      }
    }

    // Listen for window.postMessage events
    const listener = async (event: MessageEvent) => {
      // eslint-disable-next-line no-restricted-properties
      if (window.location.origin !== event.origin) return

      await handleOAuthMessage(event.data)
    }

    // Listen for BroadcastChannel messages
    const broadcastListener = async (event: MessageEvent) => {
      if (event.data.type === OAUTH_MESSAGE_TYPE) {
        await handleOAuthMessage(event.data)
      }
    }

    oauthChannel.onmessage = broadcastListener

    const timer = setInterval(function () {
      if (popup.closed) {
        // Only cleanup if we've already received the message
        if (hasReceivedMessage) {
          window.removeEventListener('message', listener)
          oauthChannel.close()
          clearInterval(timer)
          safetyTimer && clearTimeout(safetyTimer)
          visibilityHandler && document.removeEventListener('visibilitychange', visibilityHandler)
          setIsConnecting(false)
        }
      }
    }, 1000)
    window.addEventListener('message', listener)
  }

  return {
    connect,
    externalIntegration: props?.provider
      ? externalIntegrationsData?.external_integrations?.find(
          (externalIntegration) => externalIntegration.provider === props?.provider,
        )
      : externalIntegrationsData?.external_integrations?.[0],
    isConnecting,
  }
}
